import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { apiService, type LessonStandard } from "../../services/api.service";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Search, X, Plus } from "lucide-react";
import { toast } from "sonner";

interface LessonStandardsCardProps {
  lessonId: number;
}

export const LessonStandardsCard = ({ lessonId }: LessonStandardsCardProps) => {
  const [standards, setStandards] = useState<LessonStandard[]>([]);
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<string>("");
  const [allStandards, setAllStandards] = useState<LessonStandard[]>([]);
  const [allStandardsLoaded, setAllStandardsLoaded] = useState(false);
  const [allStandardsLoadFailed, setAllStandardsLoadFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLoading(true);
    apiService.getLessonStandards(lessonId)
      .then(setStandards)
      .catch(() => setStandards([]))
      .finally(() => setLoading(false));
  }, [lessonId]);

  useEffect(() => {
    apiService.getStandardFrameworks()
      .then((f) => {
        setFrameworks(f);
        if (f.length > 0) setSelectedFramework(f[0]);
      })
      .catch(() => {});
  }, []);

  // Reset loaded standards when framework changes
  useEffect(() => {
    setAllStandards([]);
    setAllStandardsLoaded(false);
    setAllStandardsLoadFailed(false);
    setSearchQuery("");
  }, [selectedFramework]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    if (searchOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen]);

  const loadAllStandards = useCallback(async () => {
    if (allStandardsLoaded || !selectedFramework) return;
    setAllStandardsLoadFailed(false);
    try {
      const results = await apiService.searchStandards("", selectedFramework);
      setAllStandards(results);
      setAllStandardsLoaded(true);
    } catch {
      setAllStandardsLoadFailed(true);
    }
  }, [allStandardsLoaded, selectedFramework]);

  const filteredResults = useMemo(() => {
    const existingIds = new Set(standards.map((s) => s.standard_id));
    const q = searchQuery.trim().toLowerCase();
    return allStandards.filter(
      (s) =>
        !existingIds.has(s.standard_id) &&
        (!q || s.code.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)),
    );
  }, [allStandards, standards, searchQuery]);

  const handleAdd = async (standard: LessonStandard) => {
    try {
      await apiService.addLessonStandard(lessonId, standard.standard_id);
      setStandards((prev) => [...prev, standard].sort((a, b) => a.code.localeCompare(b.code)));
      setSearchQuery("");
      setSearchOpen(false);
    } catch {
      toast.error("Failed to add standard");
    }
  };

  const handleRemove = async (standard: LessonStandard) => {
    try {
      await apiService.removeLessonStandard(lessonId, standard.standard_id);
      setStandards((prev) => prev.filter((s) => s.standard_id !== standard.standard_id));
    } catch {
      toast.error("Failed to remove standard");
    }
  };

  return (
    <Card className="rounded-xl border-border/60 p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-medium">Standards</h3>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Loading standards...
        </div>
      ) : (
        <div className="space-y-3">
          {/* Assigned standards */}
          <div className="flex flex-wrap gap-2">
            {standards.length === 0 && (
              <span className="text-sm text-muted-foreground">No standards assigned.</span>
            )}
            {standards.map((s) => (
              <span
                key={s.standard_id}
                className="flex items-center gap-1 rounded-full border border-border/60 bg-muted/30 px-2.5 py-1 text-xs font-medium"
                title={s.description}
              >
                {s.code}
                <button
                  type="button"
                  onClick={() => handleRemove(s)}
                  className="ml-0.5 rounded-full text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${s.code}`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>

          {/* Framework selector */}
          {frameworks.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-foreground">Framework</span>
              <select
                value={selectedFramework}
                onChange={(e) => setSelectedFramework(e.target.value)}
                className="block w-[320px] rounded border border-border/60 bg-background px-2 py-1.5 text-sm"
              >
                {frameworks.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          )}

          {/* Search to add */}
          <div ref={dropdownRef} className="relative w-[320px]">
            <span className="mb-1 block text-xs font-medium text-foreground">Add standard</span>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search standards to add..."
                value={searchQuery}
                className="pl-8 text-sm"
                disabled={!selectedFramework}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  loadAllStandards();
                  setSearchOpen(true);
                }}
              />
            </div>
            {searchOpen && (
              <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md">
                {allStandardsLoadFailed ? (
                  <div className="px-3 py-2 text-sm text-destructive">Failed to load standards. Click the input to retry.</div>
                ) : !allStandardsLoaded ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" /> Loading...
                  </div>
                ) : filteredResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">No standards found.</div>
                ) : (
                  <TooltipProvider delayDuration={300}>
                    <div className="max-h-52 overflow-auto">
                      {filteredResults.map((s) => (
                        <Tooltip key={s.standard_id}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                              onClick={() => handleAdd(s)}
                            >
                              <Plus className="size-3.5 shrink-0 text-muted-foreground" />
                              <span className="font-medium">{s.code}</span>
                              <span className="truncate text-xs text-muted-foreground">{s.description}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-64 bg-background text-foreground border border-border shadow-md">
                            <p className="font-medium">{s.code}</p>
                            <p className="mt-1 text-xs">{s.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
